import { useRef, type ChangeEvent } from "react";
import { Upload, X, Loader2, ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";

export interface ImageUploadInputProps {
  accept?: string;
  maxSizeMb?: number;
  previewUrl?: string | null;
  isUploading?: boolean;
  onFileSelect: (file: File) => void;
  onRemove?: () => void;
  label?: string;
  testIdPrefix?: string;
}

export function ImageUploadInput({
  accept = "image/png,image/jpeg,image/webp,image/gif",
  maxSizeMb = 5,
  previewUrl,
  isUploading = false,
  onFileSelect,
  onRemove,
  label = "Image",
  testIdPrefix = "image-upload",
}: ImageUploadInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > maxSizeMb * 1024 * 1024) {
      toast({
        title: "File too large",
        description: `Maximum size is ${maxSizeMb} MB.`,
        variant: "destructive",
      });
      return;
    }
    onFileSelect(file);
  }

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={handleChange}
        data-testid={`${testIdPrefix}-input`}
      />
      {previewUrl ? (
        <div className="relative inline-block">
          <img
            src={previewUrl}
            alt={label}
            className="max-h-40 w-auto rounded-md border"
            data-testid={`${testIdPrefix}-preview`}
          />
          {onRemove ? (
            <Button
              type="button"
              size="icon"
              variant="outline"
              className="absolute -top-2 -right-2"
              onClick={onRemove}
              disabled={isUploading}
              data-testid={`${testIdPrefix}-remove`}
            >
              <X className="w-4 h-4" />
            </Button>
          ) : null}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={isUploading}
          className="flex h-32 w-full items-center justify-center rounded-md border border-dashed bg-muted/40 text-sm text-muted-foreground hover-elevate active-elevate-2"
          data-testid={`${testIdPrefix}-trigger`}
        >
          {isUploading ? (
            <span className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Uploading…
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <ImageIcon className="w-4 h-4" /> Click to upload
            </span>
          )}
        </button>
      )}
      {previewUrl ? (
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => inputRef.current?.click()}
          disabled={isUploading}
          data-testid={`${testIdPrefix}-replace`}
        >
          {isUploading ? (
            <Loader2 className="w-3 h-3 mr-2 animate-spin" />
          ) : (
            <Upload className="w-3 h-3 mr-2" />
          )}
          Replace
        </Button>
      ) : null}
      <p className="text-xs text-muted-foreground">
        Accepted: PNG, JPG, WebP, GIF. Max {maxSizeMb} MB.
      </p>
    </div>
  );
}
